import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Film, Plus, Loader2, Clock, AlertTriangle, Palette, Image as ImgIcon } from "lucide-react";
import { api } from "@/lib/api";
import { OrchestratorTab } from "@/components/aina/OrchestratorTab";
import { toast } from "sonner";

const CAP_COLORS = {
  IMPLEMENTED: "bg-emerald-600", MOCK: "bg-amber-500",
  ARCHITECTURE_ONLY: "bg-stone-400", DISABLED: "bg-rose-500",
  IMPLEMENTED_LOCAL_INFERENCE: "bg-emerald-600", DETERMINISTIC_COMPOSITE: "bg-sky-600",
  MOCKED: "bg-amber-500", BLOCKED_BY_RESOURCE_LIMIT: "bg-orange-600", EXPERIMENTAL: "bg-violet-600",
};
const capStatus = (c) => c.provenance_status || c.status;

export default function AinaCreator() {
  const nav = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [caps, setCaps] = useState([]);
  const [credits, setCredits] = useState(null);
  const [active, setActive] = useState(null);
  const [unauth, setUnauth] = useState(false);

  useEffect(() => {
    api.get("/business/mine").then((r) => {
      const items = r.data?.items || r.data?.businesses || (Array.isArray(r.data) ? r.data : []);
      setBusinesses(items);
      if (items.length) setBizId(items[0].business_id);
    }).catch((e) => { if (e?.response?.status === 401) setUnauth(true); });
    api.get("/aina/creator/templates").then((r) => setTemplates(r.data.items || [])).catch(() => {});
    api.get("/aina/creator/capabilities").then((r) => setCaps(r.data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!bizId) return;
    try {
      const [p, c] = await Promise.all([
        api.get(`/aina/creator/projects?business_id=${bizId}`),
        api.get(`/aina/creator/credits/${bizId}`),
      ]);
      setProjects(p.data.items || []); setCredits(c.data);
    } catch { /* ignored */ }
  }, [bizId]);
  useEffect(() => { load(); }, [load]);

  const createProject = async () => {
    const name = window.prompt("Project name:");
    if (!name) return;
    try {
      const r = await api.post("/aina/creator/projects", {
        business_id: bizId, name, aspect: "9:16",
      });
      toast.success("Project created");
      setActive({ project: r.data.project, jobs: [], assets: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (unauth) return (
    <div className="min-h-screen bg-stone-50 grid place-items-center p-6">
      <div className="bg-white border rounded-2xl p-4 text-center" data-testid="creator-need-login">
        <div className="font-black">Sign in to use AINA Creator</div>
        <button onClick={() => nav("/")} className="mt-3 px-4 py-2 rounded-full bg-indigo-600 text-white font-black text-xs">Sign in</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="creator-root">
      <div className="sticky top-0 z-10 bg-white border-b">
        <div className="max-w-5xl mx-auto flex items-center gap-2 px-3 py-2">
          <button onClick={() => nav(-1)} className="p-1.5 rounded-full hover:bg-stone-100"><ArrowLeft size={18}/></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-700">AINA Creator · P3</div>
            <div className="font-black text-sm">Production Studio</div>
          </div>
          <select value={bizId || ""} onChange={(e) => setBizId(e.target.value)}
                  data-testid="creator-biz-picker"
                  className="text-[11px] font-black px-2 py-1 rounded-lg border bg-white">
            {businesses.map((b) => <option key={b.business_id} value={b.business_id}>{b.name || b.business_id}</option>)}
          </select>
        </div>
      </div>
      <div className="max-w-5xl mx-auto p-3 space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 flex gap-2 text-[11px]"
             data-testid="creator-mock-notice">
          <AlertTriangle size={14} className="text-amber-700 shrink-0"/>
          <div>P3 PRODUCTION STUDIO · MOCK ENGINE · No AI inference runs. External engines disabled.</div>
        </div>

        {credits && (
          <div className="bg-white border rounded-xl p-2 flex items-center justify-between" data-testid="creator-credits">
            <div className="text-[11px]"><span className="font-black">{credits.balance}</span> <span className="text-stone-500">AINA Credits (Mock)</span></div>
            <div className="text-[10px] text-stone-400 italic">In-DB only. No real money.</div>
          </div>
        )}

        {!active && (<>
          <button onClick={createProject} data-testid="creator-new-project"
                  className="w-full py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs">
            <Plus size={12} className="inline mr-1"/> New project
          </button>
          {projects.length === 0 && (
            <div className="p-4 text-center text-stone-500 text-xs italic bg-white border rounded-xl">No projects yet.</div>
          )}
          {projects.map((p) => (
            <button key={p.project_id} data-testid={`creator-project-${p.project_id}`}
                    onClick={async () => {
                      const r = await api.get(`/aina/creator/projects/${p.project_id}`);
                      setActive(r.data);
                    }}
                    className="w-full text-left bg-white border rounded-xl p-2">
              <div className="font-black text-sm">{p.name}</div>
              <div className="text-[10px] text-stone-500">{p.aspect} · {p.language}</div>
            </button>
          ))}

          <div className="grid grid-cols-3 gap-2 mt-3">
            {templates.map((t) => (
              <div key={t.id} className="bg-white border rounded-xl p-2" data-testid={`creator-tpl-${t.id}`}>
                <Film size={14} className="text-fuchsia-700"/>
                <div className="font-black text-[11px] mt-1 truncate">{t.name}</div>
                <div className="text-[10px] text-stone-500">{t.aspect} · {t.duration_s}s</div>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Capability Registry</div>
            <div className="grid grid-cols-2 gap-1">
              {caps.map((c) => (
                <div key={c.id} className="bg-white border rounded-lg px-2 py-1 flex items-center justify-between text-[11px]"
                     data-testid={`creator-cap-${c.id}`}>
                  <span className="truncate">{c.label}</span>
                  <span title={c.reason || ""} data-testid={`creator-cap-status-${c.id}`}
                        className={`text-[8px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${CAP_COLORS[capStatus(c)] || "bg-stone-400"}`}>
                    {capStatus(c).replace(/_/g, " ")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>)}

        {active && <ProjectEditor detail={active} onBack={() => { setActive(null); load(); }} onReload={load}/>}
      </div>
    </div>
  );
}

function P4GeneratePanel({ projectId }) {
  const [caps, setCaps] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [img, setImg] = useState(null);
  const [vid, setVid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");

  useEffect(() => {
    api.get("/aina/creator/p4/capabilities").then((r) => setCaps(r.data)).catch(() => {});
  }, []);

  const genImage = async () => {
    if (!prompt.trim()) { toast.error("Prompt required"); return; }
    setBusy(true); setStage("Generating image…"); setImg(null); setVid(null);
    try {
      const r = await api.post("/aina/creator/p4/image/generate", {
        project_id: projectId, prompt: prompt.trim(),
        width: 512, height: 512, steps: 15, seed: 0,
      });
      setImg(r.data.asset);
      setStage("");
      toast.success(`Image: ${r.data.result.generation_type}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setStage(""); }
    finally { setBusy(false); }
  };

  const composeVideo = async () => {
    if (!img) return;
    setBusy(true); setStage("Composing MP4 with FFmpeg…");
    try {
      const r = await api.post("/aina/creator/p4/video/compose", {
        project_id: projectId, asset_id: img.asset_id,
        duration_s: 4, width: 720, height: 720,
        motion: "ken_burns_zoom", title: prompt.slice(0, 60),
      });
      setVid(r.data.asset); setStage("");
      toast.success("Video composed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setStage(""); }
    finally { setBusy(false); }
  };

  const localReady = caps?.local_image_engine?.ready;
  const ffmpegReady = caps?.ffmpeg_video_engine?.ready;

  return (
    <div className="space-y-2" data-testid="p4-generate">
      <div className="bg-stone-50 border rounded-lg p-2 text-[10px]">
        <div className="font-black uppercase text-stone-500 mb-1">Engines</div>
        <div className="flex items-center justify-between py-0.5">
          <span>LocalImageEngine</span>
          <span className={`px-1.5 py-0.5 rounded-full text-white font-black ${localReady ? "bg-emerald-600" : "bg-amber-500"}`}>
            {localReady ? "READY" : "MOCK FALLBACK"}
          </span>
        </div>
        <div className="flex items-center justify-between py-0.5">
          <span>FFmpeg Composite</span>
          <span className={`px-1.5 py-0.5 rounded-full text-white font-black ${ffmpegReady ? "bg-emerald-600" : "bg-rose-600"}`}>
            {ffmpegReady ? "READY" : "UNAVAILABLE"}
          </span>
        </div>
        {!localReady && caps?.local_image_engine?.reasons?.length > 0 && (
          <div className="text-stone-500 italic mt-1">Reasons: {caps.local_image_engine.reasons.join(", ")}</div>
        )}
      </div>
      <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                data-testid="p4-prompt"
                placeholder="Describe the image to generate…"
                className="w-full px-2 py-1.5 rounded-lg border text-sm"/>
      <div className="flex gap-2">
        <button onClick={genImage} disabled={busy} data-testid="p4-gen-image"
                className="flex-1 py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <ImgIcon size={12} className="inline mr-1"/>}
          Generate PNG
        </button>
        <button onClick={composeVideo} disabled={busy || !img} data-testid="p4-compose-video"
                className="flex-1 py-2 rounded-full bg-indigo-600 text-white font-black text-xs disabled:opacity-50">
          <Film size={12} className="inline mr-1"/> Compose MP4
        </button>
      </div>
      {stage && <div className="text-xs text-stone-500 italic">{stage}</div>}
      {img && (
        <div className="border rounded-lg p-2 text-xs" data-testid="p4-image-result">
          <div className="flex items-center justify-between">
            <span className="font-black">PNG</span>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
              img.generation_type === "LOCAL_AI_IMAGE" ? "bg-emerald-600" : "bg-amber-500"}`}>
              {img.generation_type === "LOCAL_AI_IMAGE" ? "LOCAL AI" : "MOCK"}
            </span>
          </div>
          <img src={img.url} alt="generated" className="mt-1 rounded border max-h-64"/>
          <div className="text-[10px] text-stone-500 mt-1">
            engine: {img.engine} · inference: {String(img.inference)} · paid: {String(img.paid_provider)}
            {img.fallback_reason && <div className="italic">fallback: {img.fallback_reason}</div>}
          </div>
        </div>
      )}
      {vid && (
        <div className="border rounded-lg p-2 text-xs" data-testid="p4-video-result">
          <div className="flex items-center justify-between">
            <span className="font-black">MP4</span>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-indigo-600 text-white uppercase">
              FFMPEG COMPOSITE
            </span>
          </div>
          <video src={vid.url} controls className="mt-1 rounded border max-h-64 w-full"/>
          <div className="text-[10px] text-stone-500 mt-1">
            {vid.duration_s}s · {vid.width}x{vid.height} · {vid.motion} · inference: false
          </div>
        </div>
      )}
    </div>
  );
}

function P7Img2ImgPanel({ projectId }) {
  const [assets, setAssets] = useState([]);
  const [caps, setCaps] = useState(null);
  const [runtime, setRuntime] = useState(null);
  const [srcId, setSrcId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [neg, setNeg] = useState("");
  const [strength, setStrength] = useState(0.6);
  const [seed, setSeed] = useState(0);
  const [out, setOut] = useState(null);
  const [motionOut, setMotionOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");

  useEffect(() => {
    api.get(`/aina/creator/p7/capabilities-registry`).then((r) => setCaps(r.data)).catch(() => {});
    api.get(`/aina/creator/p8/runtime`).then((r) => setRuntime(r.data)).catch(() => {});
    api.get(`/aina/creator/p3/assets/${projectId}`).then((r) => setAssets((r.data.assets || []).filter((a) => (a.mime || "").startsWith("image/")))).catch(() => {});
  }, [projectId]);

  const run = async () => {
    if (!srcId || !prompt.trim()) { toast.error("Pick input image + prompt"); return; }
    setBusy(true); setStage("Running local img2img on CPU…"); setOut(null); setMotionOut(null);
    try {
      const r = await api.post("/aina/creator/p7/image/img2img", {
        project_id: projectId, input_asset_id: srcId, prompt: prompt.trim(),
        negative_prompt: neg || null, strength: Number(strength), seed: Number(seed),
        steps: 10, width: 384, height: 384,
      });
      setOut(r.data.asset); setStage("");
      toast.success(r.data.generation_type);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setStage(""); }
    finally { setBusy(false); }
  };

  const motion = async () => {
    if (!out) return;
    setBusy(true); setStage("Composing deterministic motion with FFmpeg…");
    try {
      const r = await api.post("/aina/creator/p7/motion/compose", {
        project_id: projectId, source_asset_id: out.asset_id,
        duration_s: 4, width: 720, height: 720, motion: "ken_burns_zoom",
        title: prompt.slice(0, 60),
      });
      setMotionOut(r.data.asset); setStage(""); toast.success("Deterministic motion MP4 ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setStage(""); }
    finally { setBusy(false); }
  };

  const i2iItem = (caps?.items || []).find((x) => x.id === "image_to_image");
  const i2iLive = i2iItem?.status === "IMPLEMENTED";

  return (
    <div className="space-y-2" data-testid="p7-img2img">
      <div className="bg-stone-50 border rounded-lg p-2 text-[10px]">
        <div className="font-black uppercase text-stone-500 mb-1">P7 engines</div>
        <div className="flex items-center justify-between py-0.5">
          <span>LocalImg2ImgEngine</span>
          <span data-testid="p7-i2i-status" className={`px-1.5 py-0.5 rounded-full text-white font-black ${i2iLive ? "bg-emerald-600" : "bg-amber-500"}`}>
            {i2iLive ? "LOCAL AI IMG2IMG" : "MOCK FALLBACK"}
          </span>
        </div>
        <div className="flex items-center justify-between py-0.5">
          <span>Deterministic Motion</span>
          <span className="px-1.5 py-0.5 rounded-full bg-indigo-600 text-white font-black">FFMPEG</span>
        </div>
        <div className="italic text-stone-500 mt-1">P7 is img2img only. NOT reference-conditioned character consistency (IP-Adapter BLOCKED_BY_DISK_CAPACITY).</div>
      </div>

      {runtime && (
        <div className="bg-stone-50 border rounded-lg p-2 text-[10px]" data-testid="p8-runtime-badge">
          <div className="flex items-center justify-between mb-1">
            <div className="font-black uppercase text-stone-500">P8 runtime</div>
            <span className={`px-1.5 py-0.5 rounded-full text-white font-black ${runtime.generation_available ? "bg-emerald-600" : "bg-amber-500"}`}>
              {runtime.generation_available ? "LOCAL AI · CPU · SAFE PROFILE" : runtime.lifecycle?.lifecycle === "DISABLED" ? "MOCK FALLBACK" : "RESOURCE-LIMITED"}
            </span>
          </div>
          <div className="text-stone-500">
            cgroup: {runtime.hardware?.cgroup_memory_max_mb ? `${runtime.hardware.cgroup_memory_max_mb} MB` : "n/a"} · headroom: {runtime.hardware?.memory_headroom_mb ?? "n/a"} MB · disk: {runtime.hardware?.disk_free_mb ?? "n/a"} MB · inflight: {runtime.admission?.inflight_count ?? 0}
          </div>
          <div className="text-stone-500">
            profile: {runtime.safe_profile?.name} · {runtime.safe_profile?.width}×{runtime.safe_profile?.height} · {runtime.safe_profile?.steps} steps · est {runtime.safe_profile?.estimated_memory_mb} MB · ~{runtime.safe_profile?.estimated_time_s}s
          </div>
          <div className="text-stone-500 italic">
            lifecycle: {runtime.lifecycle?.lifecycle} · installed: {String(runtime.lifecycle?.installed)} · enabled: {String(runtime.lifecycle?.enabled)} · safe_to_run: {String(runtime.lifecycle?.safe_to_run)}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Input image</div>
        <select value={srcId} onChange={(e) => setSrcId(e.target.value)}
                data-testid="p7-input-asset"
                className="w-full px-2 py-1.5 rounded-lg border text-sm">
          <option value="">— pick an existing image asset —</option>
          {assets.map((a) => <option key={a.asset_id} value={a.asset_id}>{a.name || a.file_name} · {a.width}×{a.height}</option>)}
        </select>
      </div>

      <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                data-testid="p7-prompt" placeholder="How should the image be transformed?"
                className="w-full px-2 py-1.5 rounded-lg border text-sm"/>
      <input value={neg} onChange={(e) => setNeg(e.target.value)}
             placeholder="Negative prompt (optional)" data-testid="p7-neg"
             className="w-full px-2 py-1.5 rounded-lg border text-xs"/>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="space-y-0.5">
          <div className="text-[10px] uppercase font-black text-stone-500">Strength: {strength}</div>
          <input type="range" min="0.1" max="0.9" step="0.05"
                  value={strength} data-testid="p7-strength"
                  onChange={(e) => setStrength(e.target.value)} className="w-full"/>
        </label>
        <label className="space-y-0.5">
          <div className="text-[10px] uppercase font-black text-stone-500">Seed</div>
          <input type="number" value={seed} onChange={(e) => setSeed(e.target.value)}
                  data-testid="p7-seed"
                  className="w-full px-2 py-1 rounded-lg border"/>
        </label>
      </div>

      <div className="flex gap-2">
        <button onClick={run} disabled={busy} data-testid="p7-run-img2img"
                className="flex-1 py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <ImgIcon size={12} className="inline mr-1"/>}
          Run img2img
        </button>
        <button onClick={motion} disabled={busy || !out} data-testid="p7-run-motion"
                className="flex-1 py-2 rounded-full bg-indigo-600 text-white font-black text-xs disabled:opacity-50">
          <Film size={12} className="inline mr-1"/> Deterministic motion
        </button>
      </div>
      {stage && <div className="text-xs text-stone-500 italic">{stage}</div>}
      {out && (
        <div className="border rounded-lg p-2 text-xs" data-testid="p7-image-result">
          <div className="flex items-center justify-between">
            <span className="font-black">PNG</span>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
              out.generation_type === "REAL_LOCAL_IMG2IMG" ? "bg-emerald-600" : "bg-amber-500"}`}>
              {out.generation_type === "REAL_LOCAL_IMG2IMG" ? "LOCAL AI IMG2IMG" : "MOCK"}
            </span>
          </div>
          <img src={out.url} alt="img2img output" className="mt-1 rounded border max-h-64"/>
          <div className="text-[10px] text-stone-500 mt-1">
            engine: {out.engine} · model: {out.model || "-"} · strength: {out.strength_requested} → {out.strength_effective ?? "n/a"} · seed: {out.seed} · inference: {String(out.inference)} · paid: {String(out.paid_provider)}
            {out.fallback_reason && <div className="italic">fallback: {out.fallback_reason}</div>}
          </div>
        </div>
      )}
      {motionOut && (
        <div className="border rounded-lg p-2 text-xs" data-testid="p7-video-result">
          <div className="flex items-center justify-between">
            <span className="font-black">MP4</span>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-indigo-600 text-white uppercase">DETERMINISTIC MOTION</span>
          </div>
          <video src={motionOut.url} controls className="mt-1 rounded border max-h-64 w-full"/>
          <div className="text-[10px] text-stone-500 mt-1">
            {motionOut.duration_s}s · {motionOut.width}x{motionOut.height} · {motionOut.motion} · inference: false
          </div>
        </div>
      )}
    </div>
  );
}

function P3Panel({ projectId }) {
  const [tab, setTab] = useState("scenes");
  const [scenes, setScenes] = useState([]);
  const [renders, setRenders] = useState([]);
  const [qc, setQc] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [caps, setCaps] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadScenes = useCallback(async () => {
    const r = await api.get(`/aina/creator/p3/scenes?project_id=${projectId}`);
    setScenes(r.data.items || []);
  }, [projectId]);
  const loadRenders = useCallback(async () => {
    const r = await api.get(`/aina/creator/p3/renders?project_id=${projectId}`);
    setRenders(r.data.items || []);
  }, [projectId]);
  const loadCaps = useCallback(async () => {
    const r = await api.get(`/aina/creator/p3/capabilities`);
    setCaps(r.data.items || []);
  }, []);

  useEffect(() => { loadScenes(); loadRenders(); loadCaps(); }, [loadScenes, loadRenders, loadCaps]);

  const addScene = async () => {
    const title = window.prompt("Scene title:");
    if (!title) return;
    try {
      await api.post("/aina/creator/p3/scenes", {
        project_id: projectId, title, duration_s: 5,
      });
      toast.success("Scene added"); loadScenes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startRender = async () => {
    setBusy(true);
    try {
      const r = await api.post("/aina/creator/p3/renders", {
        project_id: projectId, kind: "video",
      });
      toast.success("Mock render queued");
      setRenders((rs) => [r.data.job, ...rs]);
      // poll
      for (let i = 0; i < 20; i++) {
        await new Promise((res) => setTimeout(res, 700));
        const j = await api.get(`/aina/creator/p3/renders/${r.data.job.render_id}`);
        setRenders((rs) => rs.map((x) => x.render_id === j.data.job.render_id ? j.data.job : x));
        if (["COMPLETED", "FAILED", "CANCELLED"].includes(j.data.job.status)) break;
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runQc = async () => {
    try {
      const r = await api.post(`/aina/creator/p3/qc/${projectId}`);
      setQc(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const loadManifest = async () => {
    try {
      const r = await api.get(`/aina/creator/p3/manifest/${projectId}`);
      setManifest(r.data.manifest);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createTimeline = async () => {
    try {
      await api.post("/aina/creator/p3/timelines", { project_id: projectId });
      toast.success("Timeline ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const TABS = [
    ["scenes",   "Scenes"],
    ["generate", "Generate (P4)"],
    ["img2img",  "Img2Img (P7)"],
    ["renders",  "Renders"],
    ["timeline", "Timeline"],
    ["qc",       "QC"],
    ["manifest", "Manifest"],
    ["caps",     "Capabilities"],
    ["orchestrator", "Orchestrator (P11)"],
  ];

  return (
    <div className="bg-white border rounded-2xl p-3 mt-3 space-y-2" data-testid="p3-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-700">AINA Creator · P3 · Production</div>
        <div className="text-[9px] text-stone-400 italic">Mock Engine only · zero paid AI</div>
      </div>
      <div className="flex gap-1 overflow-x-auto">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`p3-tab-${k}`}
                  className={`px-2 py-1 rounded-full text-[10px] font-black uppercase whitespace-nowrap ${tab === k ? "bg-fuchsia-600 text-white" : "bg-stone-100 text-stone-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "scenes" && (
        <div className="space-y-2">
          <button onClick={addScene} data-testid="p3-add-scene"
                  className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-black text-xs">
            <Plus size={10} className="inline mr-1"/> Add scene
          </button>
          {scenes.length === 0 && <div className="text-xs text-stone-500 italic">No scenes yet.</div>}
          {scenes.map((s) => (
            <div key={s.scene_id} className="border rounded-lg p-2 text-xs" data-testid={`p3-scene-${s.scene_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-black">#{s.scene_no} · {s.title}</div>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase ${s.status === "approved" ? "bg-emerald-600 text-white" : s.locked ? "bg-indigo-600 text-white" : "bg-stone-200 text-stone-700"}`}>
                  {s.locked ? "LOCKED" : s.status}
                </span>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">{s.duration_s}s · {(s.character_ids || []).length} chars</div>
            </div>
          ))}
        </div>
      )}

      {tab === "generate" && <P4GeneratePanel projectId={projectId}/>}
      {tab === "img2img" && <P7Img2ImgPanel projectId={projectId}/>}

      {tab === "renders" && (
        <div className="space-y-2">
          <button onClick={startRender} disabled={busy} data-testid="p3-start-render"
                  className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-black text-xs disabled:opacity-50">
            {busy ? <Loader2 size={10} className="animate-spin inline mr-1"/> : <Film size={10} className="inline mr-1"/>}
            Queue mock render
          </button>
          {renders.length === 0 && <div className="text-xs text-stone-500 italic">No renders yet.</div>}
          {renders.map((j) => (
            <div key={j.render_id} className="border rounded-lg p-2 text-xs" data-testid={`p3-render-${j.render_id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase font-black text-stone-500">{j.kind} · attempts: {j.attempts}</div>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
                  j.status === "COMPLETED" ? "bg-emerald-600" :
                  j.status === "FAILED" ? "bg-rose-600" :
                  j.status === "CANCELLED" ? "bg-stone-500" :
                  j.status === "FINALIZING" ? "bg-indigo-500" :
                  j.status === "QUEUED" ? "bg-stone-400" : "bg-amber-500"}`}>
                  {j.status} {j.progress != null ? `${j.progress}%` : ""}
                </span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-stone-100">
                <div className="h-1 rounded-full bg-fuchsia-500" style={{ width: `${j.progress || 0}%` }}/>
              </div>
              {j.result?.watermark && (
                <div className="mt-1 bg-amber-50 border border-amber-200 rounded p-1 text-[10px]">
                  {j.result.watermark}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "timeline" && (
        <div className="space-y-2">
          <button onClick={createTimeline} data-testid="p3-create-timeline"
                  className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-black text-xs">
            Create / open timeline
          </button>
          <div className="text-[10px] text-stone-500 italic">Timeline editor UI is metadata-only in P3. Use the API for clip ops.</div>
        </div>
      )}

      {tab === "qc" && (
        <div className="space-y-2">
          <button onClick={runQc} data-testid="p3-run-qc"
                  className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-black text-xs">
            <AlertTriangle size={10} className="inline mr-1"/> Run production QC
          </button>
          {qc && (
            <div className="border rounded-lg p-2 text-xs" data-testid="p3-qc-report">
              <div className="flex items-center justify-between">
                <div className="font-black">Status</div>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
                  qc.status === "PASS" ? "bg-emerald-600" :
                  qc.status === "WARNING" ? "bg-amber-500" : "bg-rose-600"}`}>
                  {qc.status}
                </span>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">
                {qc.counts.BLOCKED} blocked · {qc.counts.WARNING} warnings
              </div>
              <div className="space-y-1 mt-2 max-h-48 overflow-y-auto">
                {(qc.issues || []).map((i, idx) => (
                  <div key={idx} className="border rounded p-1 text-[10px]">
                    <span className={`font-black mr-1 ${i.severity === "BLOCKED" ? "text-rose-700" : "text-amber-700"}`}>
                      [{i.severity}]
                    </span>
                    <span className="font-black">{i.code}</span> — {i.message}
                    {i.recommendation && <div className="text-stone-500 italic mt-0.5">→ {i.recommendation}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "manifest" && (
        <div className="space-y-2">
          <button onClick={loadManifest} data-testid="p3-load-manifest"
                  className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white font-black text-xs">
            Generate production manifest
          </button>
          {manifest && (
            <div className="border rounded-lg p-2 text-[10px] font-mono bg-stone-50 max-h-64 overflow-auto" data-testid="p3-manifest">
              <div>schema: {manifest.schema_version}</div>
              <div>scenes: {(manifest.scenes || []).length}</div>
              <div>shots: {(manifest.shots || []).length}</div>
              <div>characters: {(manifest.characters || []).length}</div>
              <div>locations: {(manifest.locations || []).length}</div>
              <div>props: {(manifest.props || []).length}</div>
              <div>ingredients: {(manifest.ingredients || []).length}</div>
              <div>versions: {(manifest.versions || []).length}</div>
              <div>renders: {(manifest.renders || []).length}</div>
              <div>guard.paid_providers_disabled: {String(manifest.guard?.paid_providers_disabled)}</div>
              <div>guard.external_engine_disabled: {String(manifest.guard?.external_engine_disabled)}</div>
            </div>
          )}
        </div>
      )}

      {tab === "orchestrator" && <OrchestratorTab projectId={projectId}/>}
      {tab === "caps" && (
        <div className="grid grid-cols-2 gap-1">
          {caps.map((c) => (
            <div key={c.id} className="border rounded px-2 py-1 flex items-center justify-between text-[10px]"
                 data-testid={`p3-cap-${c.id}`}>
              <span className="truncate">{c.label}</span>
              <span title={c.reason || ""} data-testid={`creator-p3-cap-status-${c.id}`}
                    className={`text-[8px] font-black px-1 py-0.5 rounded-full text-white uppercase ${CAP_COLORS[capStatus(c)] || "bg-stone-400"}`}>
                {capStatus(c).replace(/_/g, " ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectEditor({ detail, onBack, onReload }) {
  const [prompt, setPrompt] = useState("");
  const [capability, setCapability] = useState("text_to_image");
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState(detail.jobs || []);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Prompt required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/aina/creator/jobs", {
        project_id: detail.project.project_id, capability, prompt: prompt.trim(),
      });
      setJobs((js) => [r.data.job, ...js]);
      setPrompt("");
      pollJob(r.data.job.job_id);
      onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const pollJob = async (jid) => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 700));
      try {
        const r = await api.get(`/aina/creator/jobs/${jid}`);
        setJobs((js) => js.map((j) => j.job_id === jid ? r.data.job : j));
        if (r.data.job.status === "COMPLETED") break;
      } catch { break; }
    }
  };

  return (
    <div className="space-y-2" data-testid="creator-editor">
      <button onClick={onBack} className="text-xs text-stone-500 font-black">← Projects</button>
      <div className="bg-white border rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-fuchsia-700">Project</div>
        <div className="font-black text-sm">{detail.project.name}</div>
      </div>
      <div className="bg-white border rounded-2xl p-3 space-y-2">
        <select value={capability} onChange={(e) => setCapability(e.target.value)}
                data-testid="creator-capability"
                className="w-full px-2 py-1.5 rounded-lg border text-sm">
          <option value="text_to_image">Text → Image</option>
          <option value="text_to_video">Text → Video</option>
          <option value="image_to_image">Image → Image</option>
          <option value="image_to_video">Image → Video</option>
        </select>
        <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  data-testid="creator-prompt"
                  placeholder="Describe your creative…"
                  className="w-full px-2 py-1.5 rounded-lg border text-sm"/>
        <button onClick={generate} disabled={busy} data-testid="creator-generate"
                className="w-full py-2 rounded-full bg-fuchsia-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
          Generate (mock)
        </button>
      </div>
      {jobs.length === 0 && <div className="p-4 text-center text-stone-500 text-xs italic bg-white border rounded-xl">No jobs yet.</div>}
      {jobs.map((j) => (
        <div key={j.job_id} className="bg-white border rounded-xl p-2" data-testid={`creator-job-${j.job_id}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-black text-stone-500">{j.capability}</div>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase ${
              j.status === "COMPLETED" ? "bg-emerald-600" :
              j.status === "FINALIZING" ? "bg-indigo-500" :
              j.status === "QUEUED" ? "bg-stone-400" : "bg-amber-500"}`}>{j.status} {j.progress != null ? `${j.progress}%` : ""}</span>
          </div>
          <div className="text-[11px] text-stone-700 truncate">{j.prompt}</div>
          {j.status === "COMPLETED" && j.result && (
            <div className="mt-1 bg-amber-50 border border-amber-200 rounded-lg p-2 text-[10px]">
              <ImgIcon size={10} className="inline mr-1"/>{j.result.watermark}
            </div>
          )}
          <div className="mt-1 h-1 rounded-full bg-stone-100">
            <div className="h-1 rounded-full bg-fuchsia-500" style={{ width: `${j.progress || 0}%` }}/>
          </div>
        </div>
      ))}
      <P3Panel projectId={detail.project.project_id}/>
    </div>
  );
}
